import { FC, useId } from 'react'
import styles from './charts.module.scss'

interface ScoreGaugeProps {
  value: number | null
}

const SIZE = 300
const STROKE = 18
const RADIUS = (SIZE - STROKE * 2) / 2
const CENTER = SIZE / 2
// 240° of arc, open at the bottom: from 210° (lower left) clockwise round to
// -30° (lower right).
const START_ANGLE = 210
const SWEEP = 240

const point = (angle: number) => {
  const radians = (angle * Math.PI) / 180
  return {
    x: CENTER + RADIUS * Math.cos(radians),
    y: CENTER - RADIUS * Math.sin(radians),
  }
}

const arcPath = (fromAngle: number, toAngle: number) => {
  const from = point(fromAngle)
  const to = point(toAngle)
  const largeArc = fromAngle - toAngle > 180 ? 1 : 0
  return `M${from.x} ${from.y} A${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${to.x} ${to.y}`
}

// The AI Visibility Score dial: 0–100 along the arc, the score in the middle.
export const ScoreGauge: FC<ScoreGaugeProps> = ({ value }) => {
  const gradientId = useId()
  const clamped = value === null ? 0 : Math.max(0, Math.min(100, value))
  const endAngle = START_ANGLE - (clamped / 100) * SWEEP
  const knob = point(endAngle)

  return (
    <div className={styles.gauge}>
      <svg viewBox={`0 0 ${SIZE} ${SIZE * 0.86}`}>
        <defs>
          <linearGradient id={gradientId} x1="0" x2="1" y1="1" y2="0">
            <stop offset="0%" stopColor="#8080ff" />
            <stop offset="100%" stopColor="#b3b3ff" />
          </linearGradient>
        </defs>
        <path
          className={styles.gaugeTrack}
          d={arcPath(START_ANGLE, START_ANGLE - SWEEP)}
          fill="none"
          strokeLinecap="round"
          strokeWidth={STROKE}
        />
        {value !== null && clamped > 0 && (
          <path
            d={arcPath(START_ANGLE, endAngle)}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeLinecap="round"
            strokeWidth={STROKE}
          />
        )}
        {value !== null && (
          <circle
            className={styles.gaugeKnob}
            cx={knob.x}
            cy={knob.y}
            r={STROKE / 2 + 2}
            strokeWidth="4"
          />
        )}
      </svg>
      <div className={styles.gaugeValue}>
        <span className={styles.gaugeNumber}>
          {value === null ? '—' : Math.round(value)}
        </span>
        <span className={styles.gaugeMax}>/ 100</span>
      </div>
      <span className={`${styles.gaugeTick} ${styles.gaugeTickMin}`}>0</span>
      <span className={`${styles.gaugeTick} ${styles.gaugeTickMax}`}>100</span>
    </div>
  )
}
